/*
 * Ported from NorwegianDateUtil in NoCommons (https://github.com/bekkopen/NoCommons).
 * Copyright (C) 2014 - 2023 BEKK open source. Licensed under the MIT License.
 */
import { addDays, isSameDay, isWeekend } from "date-fns";

const holidayCache = new Map<number, Date[]>();

/**
 * Easter Sunday for the given year, using the Spencer Jones formula.
 * https://no.wikipedia.org/wiki/P%C3%A5skeformelen
 */
const getEasterDay = (year: number): Date => {
	const a = year % 19;
	const b = Math.floor(year / 100);
	const c = year % 100;
	const d = Math.floor(b / 4);
	const e = b % 4;
	const f = Math.floor((b + 8) / 25);
	const g = Math.floor((b - f + 1) / 3);
	const h = (19 * a + b - d - g + 15) % 30;
	const i = Math.floor(c / 4);
	const k = c % 4;
	const l = (32 + 2 * e + 2 * i - h - k) % 7;
	const m = Math.floor((a + 11 * h + 22 * l) / 451);
	const month = Math.floor((h + l - 7 * m + 114) / 31);
	const day = ((h + l - 7 * m + 114) % 31) + 1;
	return new Date(year, month - 1, day);
};

const getCachedHolidays = (year: number): Date[] => {
	const cached = holidayCache.get(year);
	if (cached) {
		return cached;
	}

	const easterDay = getEasterDay(year);
	const holidays = [
		new Date(year, 0, 1), // Første nyttårsdag
		addDays(easterDay, -7), // Palmesøndag
		addDays(easterDay, -3), // Skjærtorsdag
		addDays(easterDay, -2), // Langfredag
		easterDay, // Første påskedag
		addDays(easterDay, 1), // Andre påskedag
		new Date(year, 4, 1), // Arbeidernes dag
		new Date(year, 4, 17), // Grunnlovsdag
		addDays(easterDay, 39), // Kristi himmelfartsdag
		addDays(easterDay, 49), // Første pinsedag
		addDays(easterDay, 50), // Andre pinsedag
		new Date(year, 11, 25), // Første juledag
		new Date(year, 11, 26), // Andre juledag
	].sort((x, y) => x.getTime() - y.getTime());

	holidayCache.set(year, holidays);
	return holidays;
};

/** Norwegian public holidays for the given year, sorted by date. Returns copies, so the cache can't be mutated. */
export const getHolidays = (year: number): Date[] => getCachedHolidays(year).map((holiday) => new Date(holiday));

export const isHoliday = (date: Date): boolean => getCachedHolidays(date.getFullYear()).some((holiday) => isSameDay(holiday, date));

/** A working day is a weekday that is not a Norwegian public holiday. */
export const isWorkingDay = (date: Date): boolean => !isWeekend(date) && !isHoliday(date);

/**
 * Adds the given number of working days to the date, skipping weekends and holidays.
 * A negative number moves backwards in time. Time of day is kept.
 *
 * Example: adding 5 working days to Wednesday 04.04.2007 (day before Skjærtorsdag) yields Monday 16.04.2007.
 */
export const addWorkingDays = (date: Date, days: number): Date => {
	const step = days < 0 ? -1 : 1;
	let result = date;
	for (let i = 0; i < Math.abs(days); i++) {
		result = addDays(result, step);
		while (!isWorkingDay(result)) {
			result = addDays(result, step);
		}
	}
	return result;
};

export const subWorkingDays = (date: Date, days: number): Date => addWorkingDays(date, -days);
